<template>
  <div class="account-create-listing">
    <b-card class="mb-4">
      <h5>Thank you for listing on ZoopLocal!</h5>
      If you have any questions about the listing process,
      please <router-link to="/faq" target="_blank">view our FAQ</router-link>
      or <a href="mailto:support@zooplocal.com">send us an email.</a>
    </b-card>
    <h2 class="h4 mb-4">
      Listing Details
    </h2>
    <b-card
      class="mb-4">
      <b-form>
        <b-form-group
          id="form-group-listing-region"
          label="ZoopLocal Area"
          label-for="form-input-listing-region">
          <b-form-select
            id="form-input-listing-region"
            v-model="listing.regionSlug">
            <b-form-select-option
              v-for="region in regionsAvailable"
              :key="region.slug"
              :value="region.slug">
              {{ region.name }}
            </b-form-select-option>
          </b-form-select>
        </b-form-group>
        <b-row>
          <b-col
            :cols="12"
            :md="9">
            <b-form-group
              id="form-group-listing-title"
              label="Listing title"
              label-for="form-input-listing-title">
              <b-form-input
                id="form-input-listing-title"
                v-model="listing.title"
                type="text"
                required
                placeholder="Enter a short, descriptive title"/>
            </b-form-group>
          </b-col>
          <b-col
            :cols="12"
            :md="3">
            <b-form-group
              id="form-group-listing-price"
              label="Listing price"
              label-for="form-input-listing-price">
              <b-input-group prepend="$">
                <b-form-input
                  id="form-input-listing-price"
                  v-model="listing.price"
                  type="text"
                  inputmode="decimal"
                  required
                  :state="priceState"
                  placeholder=""
                  @blur="normalizePrice"/>
                <b-form-invalid-feedback :state="priceState">
                  {{ priceError }}
                </b-form-invalid-feedback>
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group
          id="form-group-listing-description"
          label="Listing description"
          label-for="form-input-listing-description">
           <b-form-textarea
            id="form-input-listing-description"
            v-model="listing.description"
            placeholder="Additional details about the listing (features, blemishes, etc.)"
            rows="3"
            max-rows="6"
          />
        </b-form-group>

        <b-form-group
          id="form-group-listing-category"
          label="Listing category"
          label-for="form-input-listing-category">
          <b-row>
            <b-col
              class="mb-2"
              :cols="12"
              :md="6">
              <b-form-select
                v-model="listing.categoriesId"
                @change="selectFirstSubcategory">
                <b-form-select-option
                  v-for="category in categories"
                  :key="category.id"
                  :value="category.id">
                  {{ category.name }}
                </b-form-select-option>
              </b-form-select>
            </b-col>
            <b-col
              class="mb-2"
              :cols="12"
              :md="6">
              <b-form-select
                v-model="listing.subcategoriesId">
                <b-form-select-option
                  v-for="subcategory in possibleSubcategories"
                  :key="subcategory.id"
                  :value="subcategory.id">
                  {{ subcategory.name }}
                </b-form-select-option>
              </b-form-select>
            </b-col>
          </b-row>
        </b-form-group>
      </b-form>
    </b-card>

    <h2 class="h4 mb-4">
      Item Details
    </h2>
    <b-card
      class="mb-4">
      <b-form>
        <b-row>
          <b-col
            class="mb-2"
            :cols="12"
            :md="6">
            <b-form-group
              id="form-group-item-color"
              label="Item Color"
              label-for="form-group-item-color">
              <b-form-input
                v-model="listing.color"
                placeholder="Describe the color of the item">
              </b-form-input>
            </b-form-group>
          </b-col>
          <b-col
            class="mb-2"
            :cols="12"
            :md="6">
            <b-form-group
              id="form-group-item-material"
              label="Item Material"
              label-for="form-group-item-material">
              <b-form-input
                v-model="listing.material"
                placeholder="Describe the material of the item">
              </b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col
            :cols="12"
            :md="3"
            class="mb-2">
            <b-form-group
              id="form-group-item-width"
              label="Item Width"
              label-for="form-group-item-width">
              <b-input-group
                append="inches">
                <b-form-input
                  v-model="listing.dimensionW"
                  required
                  placeholder="Width">
                </b-form-input>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col
            :cols="12"
            :md="3"
            class="mb-2">
            <b-form-group
              id="form-group-item-depth"
              label="Item Depth"
              label-for="form-group-item-depth">
              <b-input-group
                append="inches">
                <b-form-input
                  v-model="listing.dimensionD"
                  required
                  placeholder="Depth">
                </b-form-input>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col
            :cols="12"
            :md="3"
            class="mb-2">
            <b-form-group
              id="form-group-item-height"
              label="Item Height"
              label-for="form-group-item-height">
              <b-input-group
                append="inches">
                <b-form-input
                  v-model="listing.dimensionH"
                  required
                  placeholder="Height">
                </b-form-input>
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
    </b-card>

    <h2 class="mb-4 h4">
      Listing Images
    </h2>
    <b-card
      ref="listingImages"
      class="mb-4">
      <b-alert show variant="warning">
        <strong>Remember:</strong>
        Your images must accurately represent all wear,
        damages, and imperfections of your item.
        <template slot="dismiss">
          <b-button
            class="mt-n1"
            size="sm"
            variant="primary"
            squared>
            Accept and Continue
          </b-button>
        </template>
      </b-alert>
      <div v-show="isSelectingImage">
        <listing-image-uploader
          ref="imageUploader"
          :listing="listing"
          @submit="addImage"
          @select="onImageSelected"
          @cancel="onImageCancel"
        />
      </div>
      <div v-show="!isSelectingImage">
        <div
          v-if="!listing.images.length"
          class="text-center pt-2">
          You haven't uploaded any images yet. You can upload up to five images of your item.
        </div>

        <div
          v-show="listing.images.length > 0"
          class="account-create-listing__listing-images">
          <h2 class="h5">Uploaded Images</h2>
          <p>
            Reorder the images on your listing by dragging their thumbnails below.
          </p>
          <b-list-group>
            <draggable v-model="listing.images">
              <!-- eslint-disable max-len -->
              <b-list-group-item
                v-for="(image, index) in listing.images"
                :key="image.id"
                class="d-flex justify-content-between align-items-center account-create-listing__listing-image">
                <div class="d-flex align-items-center">
                  <img
                    height="75"
                    width="75"
                    style="margin-right: 15px"
                    :src="image.url">
                </div>
                <div>
                  <b-button
                    class="mr-3"
                    variant="default"
                    squared
                    @click="reorderImage(index, -1)"
                    :disabled="index === 0">
                    <font-awesome-icon
                      :icon="['fas', 'arrow-up']"/>
                  </b-button>
                  <b-button
                    class="mr-3"
                    variant="default"
                    squared
                    @click="reorderImage(index, 1)"
                    :disabled="index === listing.images.length - 1">
                    <font-awesome-icon
                      :icon="['fas', 'arrow-down']"/>
                  </b-button>
                  <b-button
                    variant="danger"
                    squared
                    @click="removeImage(index)">
                    Remove
                  </b-button>
                </div>
              </b-list-group-item>
              <!-- eslint-enable max-len -->
            </draggable>
          </b-list-group>
        </div>
        <div class="text-center py-4">
          <b-overlay
            :show="isImageUploading"
            rounded
            opacity="0.6"
            spinner-small
            spinner-variant="primary"
            class="d-inline-block"
          >
            <b-button
              variant="primary"
              squared
              @click="openImageUpload"
              :disabled="imagesLeft === 0">
              <span v-if="isImageUploading">
                Uploading
              </span>
              <span v-else-if="imagesLeft > 0">
                <font-awesome-icon
                        :icon="['fas', 'plus']"/>
                Add an image
              </span>
              <span v-else>
                You can only upload 5 images
              </span>
            </b-button>
          </b-overlay>
        </div>
      </div>
      <template
        v-slot:footer>
      </template>
    </b-card>

    <h2 class="mb-4 h4">
      Listing Features
    </h2>
    <b-card
      class="mb-4">
      <b-form>
        <b-row>
          <b-col
            :cols="12">
            <b-form-group
              id="form-group-listing-delivery"
              label="Delivery Options"
              label-for="form-input-listing-delivery">
              <b-form-checkbox
                id="form-input-listing-delivery-buyer-pick-up"
                v-model="listing.deliveryBuyerPickUp">
                Buyer Pick Up
                <span class="text-muted">
                  - The buyer can pick this item up from me
                </span>
              </b-form-checkbox>
              <b-form-checkbox
                id="form-input-listing-delivery-meet-in-middle"
                v-model="listing.deliveryMeetMiddle">
                Meet in the Middle
                <span class="text-muted">
                  - I can meet the buyer halfway
                </span>
              </b-form-checkbox>
              <b-form-checkbox
                id="form-input-listing-delivery-seller-deliver"
                v-model="listing.deliverySellerDeliver">
                Seller Delivery
                <span class="text-muted">
                  - I can deliver to the buyer
                </span>
              </b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col
            :cols="12">
            <b-form-group
              id="form-group-listing-condition"
              label="Item Condition"
              label-for="form-input-listing-condition">
              <b-form-checkbox
                id="form-input-listing-condition-pet-free"
                v-model="listing.conditionPetFree">
                Pet Free
                <span class="text-muted">
                  - This item comes from a pet-free environment
                </span>
              </b-form-checkbox>
              <b-form-checkbox
                id="form-input-listing-condition-smoke-free"
                v-model="listing.conditionSmokeFree">
                Smoke Free
                <span class="text-muted">
                  - This item comes from a smoke-free environment
                </span>
              </b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
    </b-card>
    <b-alert
      variant="danger"
      :show="!!listingError"
      dismissable>
      <strong>Error:</strong>
      <span v-if="!!listingError">
        {{ listingError.message }}
      </span>
    </b-alert>
    <b-alert
      variant="warning"
      :show="missingFields.length">
      You need to complete all fields before you can publish this listing.
      If you're not ready, you can save this listing as a draft and come back later.
      <br><br>
      The following fields are missing: <b>{{ missingFields }}.</b>
    </b-alert>
    <b-alert
      variant="danger"
      :show="!!invalidFields">
      The following fields contain invalid values: <b>{{ invalidFields }}</b>.
      You must correct these fields before you can save this listing.
    </b-alert>
    <div
      class="mb-3 text-right">
       <b-button
        :disabled="!!invalidFields"
        class="mr-2 mb-2"
        variant="outline-dark"
        squared
        @click="saveBack">
        Save as Draft
      </b-button>
      <b-button
        v-if="!listing.id || listing.status === 'Draft'"
        :disabled="missingFields.length > 0"
        class="mb-2 mr-2"
        variant="primary"
        squared
        @click="saveCheckout">
        Continue to Publish
      </b-button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Draggable from 'vuedraggable'

import ListingImageUploader from '@/components/listings/ListingImageUploader.vue'
import { parsePriceToCents, formatCentsToInput } from '@/utils/price'

export default {
  name: 'AccountCreateListing',
  components: {
    Draggable,
    ListingImageUploader,
  },
  data() {
    return {
      categories: [],
      listingFee: null,
      listingError: null,
      listing: {
        categoriesId: null,
        subcategoriesId: null,
        // Defaults from the navbar's current region. The form override does
        // NOT mutate the navbar/localStorage selection — a user can list in a
        // region other than their default without changing their browse view.
        regionSlug: null,
        title: null,
        price: null,
        description: null,
        color: null,
        material: null,
        dimensionD: null,
        dimensionW: null,
        dimensionH: null,
        images: [],
        deliveryBuyerPickUp: false,
        deliverySellerDeliver: false,
        deliveryMeetMiddle: false,
        conditionPetFree: false,
        conditionSmokeFree: false,
      },
      isSelectingImage: false,
      isImageUploading: false,
    }
  },
  computed: {
    ...mapGetters('Region', {
      currentRegion: 'current',
      regionsAvailable: 'available',
    }),
    listingFeeDollars() {
      if (!this.listingFee) return ''
      return (this.listingFee / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
    },
    priceError() {
      return parsePriceToCents(this.listing.price).error
    },
    priceState() {
      if (this.listing.price === null || this.listing.price === '') return null
      return this.priceError === null
    },
    possibleSubcategories() {
      if (!this.categories.length) return []
      return this.categories
        .filter(category => category.id === this.listing.categoriesId)[0]
        .subcategories
    },
    imagesLeft() {
      return 5 - this.listing.images.length
    },
    canBeSaved() {
      return !this.invalidFields
    },
    missingFields() {
      const requiredFields = [{
        name: 'Title',
        value: this.listing.title,
      }, {
        name: 'Price',
        value: this.listing.price,
      }, {
        name: 'Description',
        value: this.listing.description,
      }, {
        name: 'Images',
        value: this.listing.images.length,
      }, {
        name: 'Category',
        value: this.listing.categoriesId,
      }, {
        name: 'Subcategory',
        value: this.listing.subcategoriesId,
      }, {
        name: 'Width',
        value: this.listing.dimensionW,
      }, {
        name: 'Depth',
        value: this.listing.dimensionD,
      }, {
        name: 'Height',
        value: this.listing.dimensionH,
      },
      {
        name: 'Color',
        value: this.listing.color,
      },
      {
        name: 'Material',
        value: this.listing.material,
      },
      {
        name: 'Delivery Options',
        value: this.listing.deliveryBuyerPickUp
          || this.listing.deliverySellerDeliver
          || this.listing.deliveryMeetMiddle,
      }]

      return requiredFields
        .filter(field => field.value === null || field.value === '' || field.value === false)
        .map(field => field.name)
        .join(', ')
    },
    invalidFields() {
      const validators = [
        {
          name: 'Price',
          value: this.listing.price,
          validator: value => parsePriceToCents(value).error === null,
        },
        {
          name: 'Width',
          value: this.listing.dimensionW,
          validator: value => (value === null || value === '')
              || !Number.isNaN(parseFloat(value)),
        },
        {
          name: 'Depth',
          value: this.listing.dimensionD,
          validator: value => (value === null || value === '')
              || !Number.isNaN(parseFloat(value)),
        },
        {
          name: 'Height',
          value: this.listing.dimensionH,
          validator: value => (value === null || value === '')
              || !Number.isNaN(parseFloat(value)),
        },
      ]

      return validators
        .filter(field => !field.validator(field.value))
        .map(field => field.name)
        .join(', ')
    },
  },
  async created() {
    // Default to the region currently selected in the toolbar.
    this.listing.regionSlug = this.currentRegion.slug

    try {
      const { data: { categories } } = await this.$http.get('/categories')
      this.categories = categories
      if (!this.listing.categoriesId) {
        this.listing.categoriesId = this.categories[0].id
        this.listing.subcategoriesId = this.categories[0].subcategories[0].id
      }
    } catch (err) {
      console.log(err)
    }

    try {
      const result = await this.$http.get('/fees')
      this.listingFee = result.data.listings
    } catch (err) {
      console.error(err)
    }

    if (this.$route.params.id) {
      try {
        const { data: { listing } } = await this.$http.get(`/account/listing/${this.$route.params.id}`)
        this.listing = {
          ...listing,
          price: formatCentsToInput(listing.price),
        }
      } catch (err) {
        console.error(err)
      }
    }
  },
  // // eslint-disable-next-line no-unused-vars
  // async beforeRouteLeave(to, from, next) {
  //   const answer = await this.$bvModal
  //      .msgBoxConfirm('Are you sure you want to leave this page?', {
  //     title: 'You have unsaved changes',
  //     size: 'sm',
  //     buttonSize: 'sm',
  //     okVariant: 'danger',
  //     okTitle: 'Yes, discard my changes',
  //     cancelTitle: 'No, cancel',
  //     cancelVariant: 'default',
  //     footerClass: 'p-2',
  //     hideHeaderClose: true,
  //     centered: true,
  //   })
  //   if (answer) {
  //     next()
  //   } else {
  //     next(false)
  //   }
  // },
  methods: {
    async addImage(image) {
      this.isSelectingImage = false
      this.isImageUploading = true
      window.scrollTo(0, this.$refs.listingImages.offsetTop)

      try {
        const formData = new FormData()
        formData.append('image', image.data)
        const { data: { id, url } } = await this.$http.post('/images', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        this.listing.images.push({
          id,
          url,
        })
      } catch (err) {
        console.log(err)
      }
      this.isImageUploading = false
    },
    reorderImage(index, direction) {
      this.listing.images.splice(index, 0, this.listing.images.splice(index + direction, 1)[0])
    },
    async removeImage(index) {
      try {
        await this.$http.delete(`/image/${this.listing.images[index].id}`)
      } catch (err) {
        console.log(err)
      }
      this.listing.images.splice(index, 1)
    },
    openImageUpload() {
      this.$refs.imageUploader.$refs.input.click()
    },
    onImageCancel() {
      this.isSelectingImage = false
    },
    onImageSelected() {
      this.isSelectingImage = true
    },
    onImageListUpdate(event) {
      // eslint-disable-next-line max-len
      this.listing.images.splice(event.newIndex, 0, this.listings.images.splice(event.oldIndex, 1)[0])
    },
    imageIdToThumbnailUrl(id) {
      return `${this.$http.defaults.baseURL}/images/${id}`
    },
    selectFirstSubcategory() {
      this.listing.subcategoriesId = this.categories
        .filter(c => c.id === this.listing.categoriesId)[0]
        .subcategories[0]
        .id
    },
    // Reformat a valid price to "3,000.00" on blur; leave invalid input as-is.
    normalizePrice() {
      const { cents } = parsePriceToCents(this.listing.price)
      if (cents !== null) this.listing.price = formatCentsToInput(cents)
    },
    async saveListing() {
      try {
        this.listingError = null

        const { cents, error } = parsePriceToCents(this.listing.price)
        if (error) {
          this.listingError = error
          throw new Error(error)
        }

        const { data: { listing } } = await this.$http.put('/listings', {
          ...this.listing,
          price: cents,
        })
        return listing
      } catch (err) {
        console.log(err)
        this.listingError = err
        throw err
      }
    },
    async saveCheckout() {
      const listing = await this.saveListing()
      if (listing) {
        this.$router.push({
          path: '/account/listings/checkout/',
          query: {
            id: listing.id,
          },
        })
      }
    },
    async saveBack() {
      const listing = await this.saveListing()
      this.$bvToast.toast('Your listing has been saved', {
        title: 'Success',
        toaster: 'b-toaster-top-center',
        variant: 'success',
      })
      if (listing.status === 'Pending') {
        this.$router.push('/account/listings/pending')
      } else {
        this.$router.push('/account/listings/draft')
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.account-create-listing__listing-images {
  padding: 15px 0;
}

.account-create-listing__listing-image {
  cursor: grab;
}

</style>
